"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useCategories } from "@/features/categories/hooks/useCategories";
import { useNetworks } from "@/features/currencies/hooks/useNetworks";
import { useDebounce } from "@/hooks/useDebounce";
import { formatNetworkName } from "@/lib/asset-naming";
import { sortToParams, useBrowseParams, type SortKey } from "@/lib/browse/params";
export type CollectionTab = "top" | "trending" | "watchlist";
export type CollectionView = "grid" | "table";
export const COLLECTION_SORTS: readonly SortKey[] = ["newest", "oldest", "name-asc", "name-desc", "created-desc", "created-asc"];
const COLLECTION_TABS: readonly CollectionTab[] = ["top", "trending", "watchlist"];
export type CollectionFilters = {
  search?: string;
  network_id?: number;
  orderBy?: "id" | "name" | "created_at";
  order?: "asc" | "desc";
  trend?: boolean;
  topCollection?: boolean;
  watchlist?: boolean;
};
type Option = {
  value: string;
  label: string;
};

/**
 * THE /collection LISTING STATE — everything the page decides, none of how it
 * looks. Moved out of the page component unchanged:
 * - all state is URL state on the `/market` schema (`useBrowseParams`), with
 *   the `top` tab and the `grid` view as defaults;
 * - network and category are validated against their option lists once those
 *   arrive, so a stale `?network_id=` cannot filter by something unknown;
 * - a category switches the source to `/category-detail/{slug}` — with
 *   `type=collection`, because that endpoint otherwise mixes a category's
 *   collections with its NFTs (measured on `art`: 1026 rows unfiltered,
 *   334 with type=collection, 689 with type=nft);
 * - the tab maps to the `trend` / `top-collection` / `watchlist` flags.
 */
export function useCollectionsListing(isFa) {
  const $ = _c(64);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      sortKeys: COLLECTION_SORTS,
      defaultSort: "newest",
      tabs: COLLECTION_TABS,
      defaultTab: "top",
      views: ["grid", "table"],
      defaultView: "grid"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const params = useBrowseParams(t0);
  const {
    q,
    networkId,
    category,
    sort,
    view,
    tab,
    page,
    set,
    clearAll
  } = params;
  const activeTab = tab ?? "top";
  const activeView = view ?? "grid";
  const {
    data: networksData
  } = useNetworks();
  const {
    data: categoriesData
  } = useCategories("nft");
  const debouncedQuery = useDebounce(q, 500);
  let t1;
  if ($[1] !== networksData?.data?.networks) {
    t1 = networksData?.data?.networks ?? [];
    $[1] = networksData?.data?.networks;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = t1.map(_temp);
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const networkOptions = t2;
  let t3;
  if ($[5] !== categoriesData?.data?.items) {
    t3 = categoriesData?.data?.items ?? [];
    $[5] = categoriesData?.data?.items;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isFa || $[8] !== t3) {
    let t5;
    if ($[10] !== isFa) {
      t5 = c => ({
        value: c.slug,
        label: (isFa ? c.fa_name : c.name) || c.name
      });
      $[10] = isFa;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t4 = t3.map(t5);
    $[7] = isFa;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const categoryOptions = t4;
  const networkKnown = networkOptions.length === 0 || networkOptions.some(o => o.value === String(networkId));
  const effectiveNetworkId = networkId && networkKnown ? networkId : undefined;
  const categoryKnown = categoryOptions.length === 0 || categoryOptions.some(o_0 => o_0.value === category);
  const effectiveCategory = category && categoryKnown ? category : null;
  const categoryEndpoint = effectiveCategory ? `/category-detail/${encodeURIComponent(effectiveCategory)}?type=collection` : undefined;
  let t5;
  if ($[12] !== sort) {
    t5 = sortToParams(sort);
    $[12] = sort;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const {
    orderBy,
    order
  } = t5;
  const t6 = activeTab === "trending";
  const t7 = activeTab === "top";
  const t8 = activeTab === "watchlist";
  let t9;
  if ($[14] !== debouncedQuery || $[15] !== effectiveNetworkId || $[16] !== order || $[17] !== orderBy || $[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = {
      search: debouncedQuery,
      network_id: effectiveNetworkId,
      orderBy,
      order,
      trend: t6,
      topCollection: t7,
      watchlist: t8
    };
    $[14] = debouncedQuery;
    $[15] = effectiveNetworkId;
    $[16] = order;
    $[17] = orderBy;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const filters = t9;
  const hasActiveFilters = Boolean(q) || Boolean(effectiveNetworkId) || Boolean(effectiveCategory);
  const t10 = effectiveNetworkId ? String(effectiveNetworkId) : null;
  let t11;
  if ($[22] !== networkOptions || $[23] !== t10) {
    t11 = {
      options: networkOptions,
      value: t10
    };
    $[22] = networkOptions;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== categoryOptions || $[26] !== effectiveCategory) {
    t12 = {
      options: categoryOptions,
      value: effectiveCategory
    };
    $[25] = categoryOptions;
    $[26] = effectiveCategory;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  if ($[28] !== set) {
    t13 = value => set({
      q: value || null
    }, {
      history: "replace"
    });
    t14 = value_0 => set({
      sort: value_0
    });
    t15 = value_1 => set({
      tab: value_1
    });
    t16 = value_2 => set({
      view: value_2
    });
    t17 = value_3 => set({
      network_id: value_3 ? Number(value_3) : null
    });
    t18 = value_4 => set({
      category: value_4
    });
    t19 = value_5 => set({
      page: value_5
    }, {
      resetPage: false
    });
    $[28] = set;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t13 = $[29];
    t14 = $[30];
    t15 = $[31];
    t16 = $[32];
    t17 = $[33];
    t18 = $[34];
    t19 = $[35];
  }
  let t20;
  if ($[36] !== clearAll) {
    t20 = () => clearAll();
    $[36] = clearAll;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== activeTab || $[39] !== clearAll) {
    t21 = () => clearAll({
      tab: activeTab === "top" ? null : activeTab
    });
    $[38] = activeTab;
    $[39] = clearAll;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== params) {
    t22 = target => params.buildHref({
      page: target
    }, false);
    $[41] = params;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== activeTab || $[44] !== activeView || $[45] !== categoryEndpoint || $[46] !== filters || $[47] !== hasActiveFilters || $[48] !== page || $[49] !== q || $[50] !== sort || $[51] !== t11 || $[52] !== t12 || $[53] !== t13 || $[54] !== t14 || $[55] !== t15 || $[56] !== t16 || $[57] !== t17 || $[58] !== t18 || $[59] !== t19 || $[60] !== t20 || $[61] !== t21 || $[62] !== t22) {
    t23 = {
      q,
      sort,
      page,
      tab: activeTab,
      view: activeView,
      filters,
      categoryEndpoint,
      hasActiveFilters,
      network: t11,
      category: t12,
      setQuery: t13,
      setSort: t14,
      setTab: t15,
      setView: t16,
      setNetwork: t17,
      setCategory: t18,
      setPage: t19,
      clearFilters: t20,
      clearAllKeepTab: t21,
      buildPageHref: t22
    };
    $[43] = activeTab;
    $[44] = activeView;
    $[45] = categoryEndpoint;
    $[46] = filters;
    $[47] = hasActiveFilters;
    $[48] = page;
    $[49] = q;
    $[50] = sort;
    $[51] = t11;
    $[52] = t12;
    $[53] = t13;
    $[54] = t14;
    $[55] = t15;
    $[56] = t16;
    $[57] = t17;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  return t23;
}
function _temp(n) {
  return {
    value: String(n.id),
    label: formatNetworkName(n.name)
  };
}
export type CollectionsListing = ReturnType<typeof useCollectionsListing>;